import { authService } from '@/features/auth/services/authService';
import {
    Comment,
    CommentMention,
    CommentWithMentionsAndUser,
    WorkspaceMemberWithUser,
} from '../../../common/types/databaseTypes';
import { Database, supabase } from '@tickitacka/shared';
import { mentionService } from './commentMentionService';
import { workspaceService } from '@/features/workspace/services/workspaceService';

export type CommentInsert = Database['public']['Tables']['comment']['Insert'];
export type CreateCommentParams = Omit<
    CommentInsert,
    'id' | 'register_date' | 'update_date' | 'enable'
> & {
    mentions: CommentMention[];
};

export const commentService = {
    // 댓글 추가 (목표 또는 할일)
    async addComment(params: CreateCommentParams): Promise<{
        comment: Comment | null;
        mentions: CommentMention[];
    }> {
        try {
            const now = new Date().toISOString();
            const { mentions, ...commentParams } = params;

            const { data, error } = await supabase
                .from('comment')
                .insert({
                    id: crypto.randomUUID(),
                    ...commentParams,
                    register_date: now,
                    update_date: now,
                    enable: true,
                })
                .select('*')
                .single();

            if (error) throw error;

            if (mentions.length > 0) {
                await mentionService.addMentionsForComment(data.id, mentions);
            }

            return {
                comment: data as Comment | null,
                mentions,
            };
        } catch (error) {
            let targetType = 'unknown';
            let targetId: string | undefined;

            if (params.goal_id) {
                targetType = 'goal';
                targetId = params.goal_id;
            } else if (params.todo_id) {
                targetType = 'todo';
                targetId = params.todo_id;
            } else if (params.note_id) {
                targetType = 'note';
                targetId = params.note_id;
            }
            console.error(
                `Failed to add comment to ${targetType} ${targetId}`,
                error
            );
            throw error;
        }
    },

    // 댓글 조회 공통 메서드
    async getComments(
        fieldName: 'goal_id' | 'todo_id' | 'note_id',
        id: string
    ): Promise<CommentWithMentionsAndUser[]> {
        try {
            const { data, error } = await supabase
                .from('comment')
                .select(
                    `
                    *,
                    user:register_id (
                        id,
                        name,
                        profile_image_url
                    ),
                    mentions:comment_mention!comment_id (
                        id,
                        user_id,
                        comment_id,
                        mention_user:user!user_id (
                            id,
                            name,
                            profile_image_url
                        )
                    )
                `
                )
                .eq(fieldName, id)
                .eq('enable', true)
                .order('register_date', { ascending: false });

            if (error) throw error;

            // 댓글과 답글 구조화
            const comments = (data as CommentWithMentionsAndUser[]) || [];
            const rootComments: CommentWithMentionsAndUser[] = [];
            const replyMap: Record<string, CommentWithMentionsAndUser[]> = {};

            // 답글 맵 생성
            comments.forEach(comment => {
                if (comment.parent_id) {
                    if (!replyMap[comment.parent_id]) {
                        replyMap[comment.parent_id] = [];
                    }
                    replyMap[comment.parent_id].push(comment);
                } else {
                    rootComments.push(comment);
                }
            });

            // 답글 할당
            rootComments.forEach(comment => {
                if (replyMap[comment.id]) {
                    // 여기서 답글만 오름차순으로 정렬
                    replyMap[comment.id].sort(
                        (a, b) =>
                            new Date(a.register_date).getTime() -
                            new Date(b.register_date).getTime()
                    );
                    comment.children = replyMap[comment.id];
                }
            });

            return rootComments;
        } catch (error) {
            console.error(
                `Failed to get comments for ${fieldName} ${id}`,
                error
            );
            throw error;
        }
    },

    // 댓글 개수만 조회
    async getCommentsCount(
        fieldName: 'goal_id' | 'todo_id' | 'note_id',
        id: string
    ): Promise<number> {
        try {
            const { count, error } = await supabase
                .from('comment')
                .select('id', { count: 'exact', head: true })
                .eq(fieldName, id)
                .eq('enable', true);

            if (error) throw error;
            return count || 0;
        } catch (error) {
            console.error(
                `Failed to get comment count for ${fieldName} ${id}`,
                error
            );
            throw error;
        }
    },

    // 단일 댓글 조회
    async getCommentById(commentId: string): Promise<Comment> {
        try {
            const { data, error } = await supabase
                .from('comment')
                .select('*')
                .eq('id', commentId)
                .eq('enable', true)
                .maybeSingle();
            if (error) throw error;
            return data as Comment;
        } catch (error) {
            console.error(`Failed to get comment by id ${commentId}`, error);
            throw error;
        }
    },

    // 댓글 수정
    async updateComment(
        commentId: string,
        content: string,
        mentions: CommentMention[]
    ): Promise<{
        comment: Comment | null;
        addedMentionUids: string[];
        removedMentionUids: string[];
    }> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            // 댓글 내용 업데이트
            const { data, error } = await supabase
                .from('comment')
                .update({
                    content,
                    update_id: user.id,
                    update_date: new Date().toISOString(),
                })
                .eq('id', commentId)
                .select('*')
                .single();

            if (error) throw error;

            // 멘션 업데이트
            const { addedMentionUids, removedMentionUids } =
                await mentionService.updateMentionsForComment(
                    commentId,
                    mentions
                );

            return {
                comment: data as Comment | null,
                addedMentionUids,
                removedMentionUids,
            };
        } catch (error) {
            console.error(`Failed to update comment ${commentId}`, error);
            throw error;
        }
    },

    // 댓글 삭제
    async deleteComment(commentId: string): Promise<void> {
        try {
            const { error } = await supabase
                .from('comment')
                .update({
                    enable: false,
                })
                .eq('id', commentId);

            if (error) throw error;
        } catch (error) {
            console.error(`Failed to delete comment ${commentId}`, error);
            throw error;
        }
    },

    // 멘션 가능한 유저(=워크스페이스 멤버) 조회
    async getMentionableUsers(
        contentType: 'goal' | 'todo' | 'note' | 'feedback' | 'daily_scrum',
        contentId: string | undefined
    ): Promise<WorkspaceMemberWithUser[]> {
        try {
            if (!contentId) {
                return [];
            }

            let workspaceId: string | undefined;

            if (contentType === 'feedback') {
                return [];
                const { data, error } = await supabase
                    .from('feedback')
                    .select('workspace_id')
                    .eq('id', contentId)
                    .eq('enable', true)
                    .single();

                if (error || !data) {
                    console.error('피드백 조회 실패:', error);
                    return [];
                }

                workspaceId = data.workspace_id;
            } else if (contentType === 'daily_scrum') {
                return [];
                const { data, error } = await supabase
                    .from('daily_scrum')
                    .select('team_id')
                    .eq('id', contentId)
                    .single();

                if (error || !data) {
                    console.error('데일리 스크럼 조회 실패:', error);
                    return [];
                }

                const { data: workspaceData, error: workspaceError } =
                    await supabase
                        .from('team')
                        .select('workspace_id')
                        .eq('id', data.team_id)
                        .single();

                if (workspaceError || !workspaceData) {
                    console.error('워크스페이스 조회 실패:', workspaceError);
                    return [];
                }

                workspaceId = workspaceData.workspace_id;
            } else if (contentType === 'goal') {
                // goal의 경우 직접 워크스페이스 조회
                const workspace =
                    await workspaceService.getWorkspaceByGoalId(contentId);
                workspaceId = workspace?.id;
            } else {
                // todo나 note인 경우
                let goalId: string | undefined;

                if (contentType === 'todo') {
                    // todo의 경우 goal_id만 조회
                    const { data, error } = await supabase
                        .from('todo')
                        .select('goal_id')
                        .eq('id', contentId)
                        .eq('enable', true)
                        .single();

                    if (error || !data) {
                        console.error('할 일 조회 실패:', error);
                        return [];
                    }

                    goalId = data.goal_id;
                } else {
                    // note인 경우 goal_id와 todo_id를 모두 조회
                    const { data, error } = await supabase
                        .from('note')
                        .select('goal_id, todo_id')
                        .eq('id', contentId)
                        .eq('enable', true)
                        .single();

                    if (error || !data) {
                        console.error('노트 조회 실패:', error);
                        return [];
                    }

                    if (data.todo_id) {
                        // todo_id가 있는 경우 해당 todo를 통해 goal_id 조회
                        const { data: todoData, error: todoError } =
                            await supabase
                                .from('todo')
                                .select('goal_id')
                                .eq('id', data.todo_id)
                                .eq('enable', true)
                                .single();

                        if (!todoError && todoData) {
                            goalId = todoData.goal_id;
                        } else {
                            console.error(
                                '노트 연결된 할 일 조회 실패:',
                                todoError
                            );
                        }
                    }

                    // todo_id를 통한 조회가 실패했거나 goal_id가 있는 경우
                    if (!goalId && data.goal_id) {
                        goalId = data.goal_id;
                    }
                }

                // 조회된 goal_id로 워크스페이스 조회
                if (goalId) {
                    const workspace =
                        await workspaceService.getWorkspaceByGoalId(goalId);
                    workspaceId = workspace?.id;
                }
            }

            if (!workspaceId) {
                console.warn(
                    '워크스페이스 ID를 찾을 수 없습니다. (워크스페이스와 연결되지 않은 컨텐츠일 수도 있음)'
                );
                return [];
            }

            // 워크스페이스 멤버 조회
            return await workspaceService.getWorkspaceMembers(workspaceId);
        } catch (error) {
            console.error('멘션 가능한 유저 조회 중 오류 발생:', error);
            return [];
        }
    },
};
